
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { QrCode, Download, Share2, ArrowLeft, Watch, Eye } from 'lucide-react';
import QRCodeLib from 'qrcode';
import { QRMedicalDataSelector } from './QRMedicalDataSelector';
import { WatchBandQRPopup } from './WatchBandQRPopup';

interface MedicalQRCodeProps {
  selectedPatientId: string;
}

export const MedicalQRCode = ({ selectedPatientId }: MedicalQRCodeProps) => {
  const [currentStep, setCurrentStep] = useState<'initial' | 'select' | 'display'>('initial');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [publicUrl, setPublicUrl] = useState<string>('');  
  const [profileId, setProfileId] = useState<string>('');
  const [qrImageUrl, setQrImageUrl] = useState<string>('');
  const [showWatchBandPopup, setShowWatchBandPopup] = useState(false);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);

  useEffect(() => {
    checkForExistingProfile();
  }, [selectedPatientId]);

  const checkForExistingProfile = () => {
    const storageKey = selectedPatientId === 'me' ? 'public_profile_current_user' : `public_profile_${selectedPatientId}`;
    const existingProfile = localStorage.getItem(storageKey);
    
    if (existingProfile) {
      try {
        const profileData = JSON.parse(existingProfile);
        if (profileData.publicUrl && profileData.profileId) {
          setHasExistingProfile(true);
          setPublicUrl(profileData.publicUrl);
          setProfileId(profileData.profileId);
        }
      } catch (error) {
        console.error('Error parsing existing profile:', error);
        setHasExistingProfile(false);
      }
    } else {
      setHasExistingProfile(false);
    }
  };

  const handleViewExistingQR = async () => {
    if (publicUrl) {
      try {
        // Generate QR code image for existing profile
        const qrImage = await QRCodeLib.toDataURL(publicUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrImageUrl(qrImage);
        setQrCodeUrl(publicUrl);
        setCurrentStep('display');
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  };

  const handleProfileCreated = async (newProfileId: string, newQrUrl: string) => {
    setProfileId(newProfileId);
    setQrCodeUrl(newQrUrl);
    
    // Get the public URL from localStorage
    const storageKey = selectedPatientId === 'me' ? 'public_profile_current_user' : `public_profile_${selectedPatientId}`;
    const storedProfile = localStorage.getItem(storageKey);
    if (storedProfile) {
      try {
        const profileData = JSON.parse(storedProfile);
        setPublicUrl(profileData.publicUrl || newQrUrl);
      } catch (error) {
        console.error('Error parsing stored profile:', error);
        setPublicUrl(newQrUrl);
      }
    } else {
      setPublicUrl(newQrUrl);
    }
    
    try {
      // Generate QR code image
      const qrImage = await QRCodeLib.toDataURL(newQrUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrImageUrl(qrImage);
      setCurrentStep('display');
      setHasExistingProfile(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleDownload = () => {
    if (qrImageUrl) {
      const link = document.createElement('a');
      link.href = qrImageUrl;
      link.download = `medical-qr-${profileId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = async () => {
    if (navigator.share && qrCodeUrl) {
      try {
        await navigator.share({
          title: 'Medical QR Code',
          text: 'My medical information QR code',
          url: qrCodeUrl
        });
      } catch (error) {
        console.error('Error sharing:', error);
        // Fallback to copying to clipboard
        navigator.clipboard.writeText(qrCodeUrl);
      }
    } else if (qrCodeUrl) {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(qrCodeUrl);
    }
  };

  const handleWatchBandPrint = () => {
    setShowWatchBandPopup(true);
  };

  const handleBackToInitial = () => {
    setCurrentStep('initial');
  };

  const handleBackToSelection = () => {
    setCurrentStep('select');
    setQrCodeUrl('');
    setQrImageUrl('');
  };

  const handleGenerateNewQR = () => {
    setCurrentStep('select');
  };

  if (currentStep === 'display' && qrImageUrl) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Your Medical QR Code</h2>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={handleBackToSelection}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Edit Selection
              </Button>
              <Button variant="outline" onClick={handleBackToInitial}>
                Back to Menu
              </Button>
            </div>
          </div>
          
          <div className="text-center space-y-6">
            <div className="inline-block p-4 bg-white rounded-lg shadow-sm">
              <img src={qrImageUrl} alt="Medical QR Code" className="mx-auto" />
            </div>
            
            <div className="space-y-2">
              <p className="text-lg font-medium">Medical Emergency QR Code</p>
              <p className="text-muted-foreground">
                Emergency responders can scan this code to access your critical medical information
              </p>
              {publicUrl && (
                <p className="text-sm text-muted-foreground break-all">
                  Public URL: {publicUrl}
                </p>
              )}
            </div>

            <div className="flex justify-center space-x-4">
              <Button onClick={handleDownload} className="flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Download</span>
              </Button>
              <Button variant="outline" onClick={handleShare} className="flex items-center space-x-2">
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </Button>
              <Button variant="outline" onClick={handleWatchBandPrint} className="flex items-center space-x-2">
                <Watch className="h-4 w-4" />
                <span>Print for Watch Band</span>
              </Button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Keep this QR code accessible for emergencies. 
                Consider printing it and keeping it in your wallet, or saving it to your phone's home screen.
              </p>
            </div>
          </div>
        </Card>

        <WatchBandQRPopup
          isOpen={showWatchBandPopup}
          onClose={() => setShowWatchBandPopup(false)}
          qrImageUrl={qrImageUrl}
          publicUrl={publicUrl}
          profileId={profileId}
        />
      </div>
    );
  }

  if (currentStep === 'select') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4 mb-4">
          <Button variant="outline" onClick={handleBackToInitial}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold">Generate Medical QR Code</h2>
            <p className="text-muted-foreground">
              Select your essential medical information for emergency situations
            </p>
          </div>
        </div>

        <QRMedicalDataSelector 
          selectedPatientId={selectedPatientId}
          onProfileCreated={handleProfileCreated}
        />
      </div>
    );
  }

  // Initial view
  return (
    <div className="space-y-6">
      <div className="text-center">
        <QrCode className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h2 className="text-2xl font-bold">Medical QR Code</h2>
        <p className="text-muted-foreground">
          Create or view your medical emergency QR code for quick access to critical health information
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          {hasExistingProfile ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  You have an existing medical QR code profile
                </p>
                <div className="flex justify-center space-x-4">
                  <Button onClick={handleViewExistingQR} className="flex items-center space-x-2">
                    <Eye className="h-4 w-4" />
                    <span>View QR Code</span>
                  </Button>
                  <Button variant="outline" onClick={handleGenerateNewQR} className="flex items-center space-x-2">
                    <QrCode className="h-4 w-4" />
                    <span>Update QR Code</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Create a QR code with your essential medical information for emergency situations
              </p>
              <Button onClick={handleGenerateNewQR} className="flex items-center space-x-2">
                <QrCode className="h-4 w-4" />
                <span>Generate QR Code</span>
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
